import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { css } from "@styled-system/css";
import { FormikProps } from "formik";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useMemo } from "react";

import {
  Alert,
  Box,
  Button,
  InputDate,
  Select,
  Text,
} from "~/components/blocks";
import { PharmacyProfile } from "~/components/partials";
import { AppDownloadBanner } from "~/components/partials/AppDownloadBanner";

import { useFetchPharmacy } from "../../use-fetch-pharmacy";
import { Fields } from "../types";
import { useResetErrors } from "../use-reset-errors";
import { useAvailableReservation } from "./use-available-reservation";

type Props = {
  pharmacyId?: string;
  formik: FormikProps<Fields>;
  onClick: () => void;
};

const Profile = styled(Box)(({ theme }) =>
  css({
    background: theme.colors.background.gradation,
    padding: theme.space.xl,
  }),
);

const Wrap = styled(Box)(({ theme }) =>
  css({
    width: "100%",
    maxWidth: theme.windowSize.base,
    margin: "0 auto",
    padding: "0",
    boxSizing: "border-box",
  }),
);

export const SelectReservationTime = React.memo((props: Props) => {
  const { formik } = props;
  const router = useRouter();
  const theme = useTheme();

  const { loading, hasError, pharmacy } = useFetchPharmacy(props.pharmacyId);
  const { isSelectableDate, timeOptions, handleFilter, handleSelectDate } =
    useAvailableReservation(pharmacy?.webBookingsSetting.slots || []);

  const isInputted =
    formik.values.date && formik.values.startTime && formik.values.endTime;

  const time = useMemo(() => {
    const _option = timeOptions.find(
      (option) =>
        option.startTime === formik.values.startTime &&
        option.endTime === formik.values.endTime,
    );

    return _option?.value;
  }, [formik.values.endTime, formik.values.startTime, timeOptions]);

  const handleChangeDate = useCallback(
    async (date: Date | null) => {
      if (date) {
        await formik.setFieldValue("date", date);
        handleSelectDate(date);
      }
    },
    [formik, handleSelectDate],
  );
  const handleChangeTime = useCallback(
    async (event: React.ChangeEvent<HTMLSelectElement>) => {
      const value = event.currentTarget.value;
      const _option = timeOptions.find((option) => option.value === value);

      if (_option) {
        await formik.setValues({
          ...formik.values,
          startTime: _option.startTime,
          endTime: _option.endTime,
        });
      }
    },
    [formik, timeOptions],
  );

  useResetErrors(formik);
  useEffect(() => {
    if (formik.values.date) {
      handleChangeDate(formik.values.date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timeOption = timeOptions.find(
      (option) =>
        option.startTime === formik.values.startTime &&
        option.endTime === formik.values.endTime,
    );

    if (!timeOption && formik.values.startTime && formik.values.endTime) {
      formik.setValues({
        ...formik.values,
        startTime: "",
        endTime: "",
      });
    }
  }, [formik, timeOptions]);

  if (router.isReady && !loading && hasError) {
    router.push("/404");
  }

  return (
    <Box>
      <Profile>
        <Wrap>
          <PharmacyProfile fontColor="white" pharmacy={pharmacy} />
        </Wrap>
      </Profile>
      <Box padding={theme.space.l}>
        <Wrap>
          <Text size="m" wordBreak="break-all" whiteSpace="pre-wrap">
            {pharmacy?.webBookingsSetting.description}
          </Text>
          {pharmacy != null && (
            <Box marginTop={theme.space.l} maxWidth="400px">
              <AppDownloadBanner
                page="select_reservation_time"
                pharmacy={pharmacy}
              />
            </Box>
          )}
          {loading || isSelectableDate ? (
            <Box marginTop={theme.space.xl}>
              <Text block size="m" fontWeight="bold">
                お薬の受け取り希望日時を選択してください
              </Text>
              <Text block size="m">
                処方箋の有効期限は発行日を含めて4日以内です。期限内に受け取り可能な日時となっているかご確認ください
              </Text>
              <InputDate
                placeholder="希望日を選択してください"
                marginTop={theme.space.m}
                error={!!formik.errors.date}
                value={formik.values.date}
                filterDate={handleFilter}
                onChange={handleChangeDate}
              />
              <Select
                fill
                disabled={!formik.values.date}
                marginTop={theme.space.m}
                error={formik.errors.startTime || formik.errors.endTime}
                value={time}
                onChange={handleChangeTime}
              >
                <option hidden>希望時間を選択してください</option>
                {timeOptions.map(({ value }, idx) => (
                  <option key={idx} value={value}>
                    {value}
                  </option>
                ))}
              </Select>
              <Button
                use="base"
                wide="fill"
                marginTop={theme.space.xl}
                disabled={!isInputted}
                onClick={props.onClick}
              >
                {pharmacy?.webBookingsSetting?.electronicPrescription
                  ? "次は処方箋または処方内容（控え）の登録"
                  : "次は処方箋の登録"}
              </Button>
            </Box>
          ) : (
            <Alert status="warning" marginTop={theme.space.m}>
              選択可能な日時がありません
            </Alert>
          )}
        </Wrap>
      </Box>
    </Box>
  );
});

SelectReservationTime.displayName = "SelectReservationTime";
